import { Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../../../environments/environment';
import { NotificationService } from '../../../shared/services/notification.service';
import { unwrapV2 } from '../../../shared/utils/v2-api-response.util';

interface GrantRow { grantUid: string; provider: string; amount: number; percent: number; }
interface ProjectRow { projectUid: string; projectName: string; mode: 'percentage' | 'hours'; value: number; grants: GrantRow[]; }

// Reparto automático de los gastos de un proveedor: dentro de cada proyecto con reparto, % por financiación
// ("escalera"). El % por proyecto es el que ya se configura en el formulario del contacto; con el check activo,
// el formulario de gasto puede generar una línea por proyecto y financiación (SQL V112).
@Component({
  selector: 'app-contact-auto-distribution',
  templateUrl: './contact-auto-distribution.component.html',
  styleUrls: ['./contact-auto-distribution.component.css'],
  standalone: false,
})
export class ContactAutoDistributionComponent implements OnInit {
  @Input() contactUid = '';

  loading = true;
  loadError = false;
  saving = false;
  saveError = '';

  enabled = false;
  rows: ProjectRow[] = [];

  constructor(private http: HttpClient, private notify: NotificationService) { }

  ngOnInit(): void {
    this.load();
  }

  private get url(): string {
    return `${environment.apiUrl}/api/v2/contacts/${this.contactUid}/auto-distribution`;
  }

  // Solo los proyectos con reparto (valor > 0) entran en la escalera.
  get usedRows(): ProjectRow[] {
    return this.rows.filter(r => (Number(r.value) || 0) > 0);
  }

  grantsTotal(row: ProjectRow): number {
    return row.grants.reduce((sum, g) => sum + (Number(g.percent) || 0), 0);
  }

  get problem(): string {
    if (!this.enabled) return '';
    if (!this.usedRows.length) return 'Indica antes el reparto por proyecto del proveedor (y guarda el contacto).';
    for (const row of this.usedRows) {
      if (!row.grants.length) return `El proyecto «${row.projectName}» no tiene financiaciones a las que repartir el gasto.`;
      if (Math.abs(this.grantsTotal(row) - 100) > 0.01) return `El reparto por financiación de «${row.projectName}» debe sumar 100 %.`;
    }
    return '';
  }

  onToggle(): void {
    this.saveError = '';
    // Con una sola financiación elegible el reparto interno es evidente: 100 %.
    if (this.enabled) this.usedRows.forEach(r => { if (r.grants.length === 1 && !r.grants[0].percent) r.grants[0].percent = 100; });
  }

  retry(): void {
    this.load();
  }

  save(): void {
    if (this.saving) return;
    this.saveError = '';
    const problem = this.problem;
    if (problem) {
      this.saveError = problem;
      return;
    }

    const body = {
      enabled: this.enabled,
      projects: this.usedRows.map(r => ({
        projectUid: r.projectUid,
        grants: r.grants.filter(g => (Number(g.percent) || 0) > 0).map(g => ({ grantUid: g.grantUid, percent: Number(g.percent) })),
      })),
    };

    this.saving = true;
    unwrapV2(firstValueFrom(this.http.put<any>(this.url, body)), 'No se pudo guardar el reparto automático')
      .then(() => this.notify.ok('Reparto automático guardado correctamente'))
      .catch(error => { this.saveError = (error && error.error && error.error.message) || 'No se pudo guardar el reparto automático.'; })
      .then(() => { this.saving = false; });
  }

  private load(): void {
    this.loading = true;
    this.loadError = false;
    unwrapV2(firstValueFrom(this.http.get<any>(this.url)), 'No se pudo cargar el reparto automático')
      .then((dto: any) => {
        this.enabled = !!dto.enabled;
        this.rows = (dto.projects || []).map((p: any) => ({
          projectUid: p.projectUid,
          projectName: p.projectName,
          mode: p.mode,
          value: Number(p.value) || 0,
          grants: (p.grants || []).map((g: any) => ({ grantUid: g.grantUid, provider: g.provider, amount: g.amount, percent: Number(g.percent) || 0 })),
        }));
      })
      .catch(() => { this.loadError = true; })
      .then(() => { this.loading = false; });
  }
}
